import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {isEqual} from 'lodash';
import React, {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {useLocation} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import {EVALUATION_TABLES_PANEL_TYPE} from '../../../components/PanelEvaluationTables/common';
import {WEAVE_TRACE_PANEL_TYPE} from '../../../components/PanelWeaveTrace/common';
import {useSelector} from '../../../state/hooks';
import {usePanelComments} from '../../../state/reports/usePanelComments';
import {type Ref as PanelRef} from '../../../state/views/panel/types';
import {type Ref as PanelBankSectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import {getDndSortablePanelId} from '../../PanelBank/sectionLayout/utils';
import {useWorkspaceSectionContext} from '../../PanelBank/sectionLayout/WorkspaceSectionContextProvider';
import {useWorkspaceSectionLayoutConfigs} from '../../PanelBank/sectionLayout/WorkspaceSectionLayoutConfigs';
import {PanelCompRedux} from '../../PanelCompRedux';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useConfigXBounds} from '../hooks/hooks';
import {PanelContextProvider, usePanelContext} from '../PanelContextProvider';
import {PanelEditorContainer} from '../PanelEditor/PanelEditorContainer';
import {PanelIDContextProvider} from '../PanelIDContext';
import {SharedPanelStateContextProvider} from '../SharedPanelStateContext';
import {PanelPopupMode} from '../types';
import {usePanelViewTypeClass} from '../usePanelViewTypeClass';
import {useIsWorkspacePanelInView} from './useIsWorkspacePanelInView';
import {WorkspacePanelHeader} from './WorkspacePanelHeader';

type WorkspacePanelContainerProps = {
  isProjectReadOnly: boolean;
  panelBankSectionConfigRef: PanelBankSectionRef;
  panelRef: PanelRef;
};

/** Lightweight context to pass DnD state from the outer container to
 *  WorkspacePanel without requiring it to subscribe to dnd-kit context. */
type PanelDndState = {
  isActivePanel: boolean;
  isDragging: boolean;
  hasOver: boolean;
};
const DEFAULT_DND_STATE: PanelDndState = {
  isActivePanel: false,
  isDragging: false,
  hasOver: false,
};
const PanelDndContext = createContext<PanelDndState>(DEFAULT_DND_STATE);
export const usePanelDndState = () => useContext(PanelDndContext);

/**
 * Outer wrapper that handles DnD sorting. useSortable subscribes to dnd-kit
 * context which updates on every pointer move during drag. By isolating it
 * here, only this lightweight div re-renders on drag — the expensive chart
 * content inside WorkspacePanelInner is shielded by memo.
 */
export const WorkspacePanelContainer = ({
  isProjectReadOnly,
  panelBankSectionConfigRef,
  panelRef,
}: WorkspacePanelContainerProps) => {
  const {setNodeRef, transform, transition, isDragging, over, active} =
    useSortable({
      id: getDndSortablePanelId(panelRef),
      data: {
        type: 'panel',
        sectionRef: panelBankSectionConfigRef,
        panelRef: panelRef,
      },
      animateLayoutChanges: () => false,
    });

  const isActivePanel = active?.data?.current?.panelRef?.id === panelRef.id;
  const anyDragging = active != null;
  const hasOver = over != null;

  return (
    <div
      ref={setNodeRef}
      className={twMerge(
        'relative h-full w-full',
        'line-plot-panel-crosshair-hover',
        isDragging ? 'opacity-60' : 'opacity-100'
      )}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}>
      <WorkspacePanelInner
        isProjectReadOnly={isProjectReadOnly}
        panelBankSectionConfigRef={panelBankSectionConfigRef}
        panelRef={panelRef}
        isActivePanel={isActivePanel}
        isDragging={anyDragging}
        hasOver={hasOver}
      />
    </div>
  );
};

type WorkspacePanelInnerProps = WorkspacePanelContainerProps & {
  isActivePanel: boolean;
  isDragging: boolean;
  hasOver: boolean;
};

const WorkspacePanelInner = memo(function WorkspacePanelInner({
  isProjectReadOnly,
  panelBankSectionConfigRef,
  panelRef,
  isActivePanel,
  isDragging,
  hasOver,
}: WorkspacePanelInnerProps) {
  const stablePanelId = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id]?.__id__
  );

  const dndState = useMemo(
    () => ({isActivePanel, isDragging, hasOver}),
    [isActivePanel, isDragging, hasOver]
  );

  if (stablePanelId === undefined) {
    return null;
  }

  return (
    <PanelDndContext.Provider value={dndState}>
      <PanelContextProvider
        isProjectReadOnly={isProjectReadOnly}
        panelBankSectionConfigRef={panelBankSectionConfigRef}
        panelRef={panelRef}>
        <PanelIDContextProvider stablePanelId={stablePanelId}>
          <WorkspacePanel />
        </PanelIDContextProvider>
      </PanelContextProvider>
    </PanelDndContext.Provider>
  );
});

export const WorkspacePanel = () => {
  const {shouldRenderContent} = useWorkspaceSectionContext();
  const {isReadOnly, panelRef, panel, setPanelPopupMode} = usePanelContext();

  const location = useLocation();
  const panelId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('panelId');
  }, [location.search]);

  const configXBounds = useConfigXBounds(panelRef);

  const panelType = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].viewType
  );

  // Open panel editor only if panelId matches the current panel's ID
  useEffect(() => {
    if (panelId === panel.__id__) {
      setPanelPopupMode(PanelPopupMode.FULL_SCREEN);
    } else {
      setPanelPopupMode(undefined);
    }
  }, [panelId, panel.__id__, setPanelPopupMode]);

  const {isHighlighted} = usePanelComments({panelRef});

  // DnD state is provided by WorkspacePanelInner via PanelDndContext.
  // This avoids subscribing to dnd-kit context directly, which would
  // re-render every panel's chart on every pointer move during drag.
  const {isActivePanel, isDragging, hasOver: over} = usePanelDndState();

  const panelViewTypeClass = usePanelViewTypeClass(panelType);

  const {targetPanelRef} = useWorkspaceDrawerContext();
  const isWorkspaceDrawerTarget = useMemo(
    () => isEqual(panelRef, targetPanelRef),
    [panelRef, targetPanelRef]
  );

  const {panelWidth, panelHeight} = useWorkspaceSectionLayoutConfigs();

  const {ref: domRef, shouldMountPanel} = useIsWorkspacePanelInView();
  const shouldHaveTealOutline = isWorkspaceDrawerTarget || isHighlighted;

  return (
    <SharedPanelStateContextProvider config={configXBounds}>
      <PanelEditorContainer currentHeight={0} />
      <div
        className={twMerge(
          'flex flex-col',
          'h-full w-full',
          'px-8 pb-12 pt-4',
          'bg-wb-surface',
          'rounded-md border-[1px] border-wb-border-strong hover:border-[light-dark(var(--moon-350),var(--moon-600))]',
          'transition-[box-shadow,margin-top] duration-300',
          // Panel type specific class
          panelViewTypeClass,
          // Conditional states
          !shouldRenderContent && 'opacity-50 [&>*]:hidden',
          isActivePanel && 'border-wb-accent',
          // Unfortunately, we set box-shadow to none in dark mode, so only border will appear in dark mode
          shouldHaveTealOutline &&
            'border-wb-accent shadow-[0px_0px_0px_3px_light-dark(#A9EDF2,var(--teal-700))]',
          // Don't show crosshair tooltip on panels while dragging or resizing
          isActivePanel || isDragging || over
            ? '!pointer-events-none'
            : 'cursor-default'
        )}
        data-dd-privacy={DD_PRIVACY_ALLOW}
        data-test="workspace-panel-container"
        style={{
          width: panelWidth,
          height: panelHeight,
        }}>
        <WorkspacePanelHeader />
        <div
          ref={domRef}
          className={twMerge(
            'flex flex-col',
            'w-full min-h-0 grow-[1]',
            'justify-center',
            // Applying this to other panels may cause infinite ResizeObserver loop. Please proceed with caution
            // if you are loosening this check.
            (panelType === 'Code Comparer' ||
              panelType === 'Media Browser' ||
              panelType === 'Markdown Panel') &&
              'overflow-auto',
            (panelType === WEAVE_TRACE_PANEL_TYPE ||
              panelType === EVALUATION_TABLES_PANEL_TYPE) &&
              'overflow-y-hidden'
          )}
          id={'editable-panel-' + panel.__id__}>
          {/*
              This is the expensive parts of panel rendering. If it's not in view, don't render it.
              By wrapping it directly here, there's less loader flickering which iprovides a smoother experience when scrolling
              up and down the page.
            */}
          {shouldMountPanel ? (
            <PanelCompRedux
              configMode={false}
              currentHeight={0}
              dimensions={undefined as any}
              panelRef={panelRef}
              readOnly={isReadOnly}
            />
          ) : (
            <div className="h-full w-full" />
          )}
        </div>
      </div>
    </SharedPanelStateContextProvider>
  );
};
